import React, { useState, useRef, useEffect } from 'react';
import api from '../services/api';

const healthColor  = { HEALTHY: '#059669', WARNING: '#d97706', CRITICAL: '#dc2626', STOPPED: '#94a3b8', UNKNOWN: '#6b7280' };
const healthBg     = { HEALTHY: '#d1fae5', WARNING: '#fef3c7', CRITICAL: '#fee2e2', STOPPED: '#f1f5f9', UNKNOWN: '#f8fafc' };
const healthIcon   = { HEALTHY: '✅', WARNING: '⚠️', CRITICAL: '🔴', STOPPED: '⏹️', UNKNOWN: '❓' };
const pressureColor = { LOW: '#059669', MEDIUM: '#d97706', HIGH: '#dc2626' };

const TABS = ['Health Dashboard', 'Chat with CF Apps'];

const SUGGESTIONS = [
  'Why is hr-cap-project slow today?',
  'What errors are in the logs for hr-cap-project-srv?',
  'Is hr-cap-project-approuter healthy?',
  'Show me recent crashes in my CF apps',
];

export default function CfHealthDashboard() {
  const [tab, setTab]             = useState(0);
  const [apps, setApps]           = useState([]);
  const [pressure, setPressure]   = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [filterHealth, setFilterHealth] = useState('');

  // Chat state
  const [messages, setMessages]   = useState([
    { role: 'assistant', text: 'Hi! Ask me anything about your CF apps — I\'ll fetch live logs and status to answer.' }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');
  const [projects, setProjects]   = useState([]);
  const chatBottomRef = useRef(null);

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const load = async (forceRefresh = false) => {
    setLoading(true);
    setError('');
    try {
      const [dashRes, pressRes] = await Promise.all([
        api.get(`/cf-health/dashboard?refresh=${forceRefresh}`),
        api.get('/cf-health/space-pressure'),
      ]);
      setApps(dashRes.data);
      setPressure(pressRes.data);
      setLastRefresh(new Date());
    } catch (e) {
      setError('Failed to load: ' + (e.response?.data?.message || e.message));
    }
    setLoading(false);
  };

  const summary = {
    total:   apps.length,
    healthy: apps.filter(a => a.health === 'HEALTHY').length,
    warning: apps.filter(a => a.health === 'WARNING').length,
    critical:apps.filter(a => a.health === 'CRITICAL').length,
    stopped: apps.filter(a => a.health === 'STOPPED').length,
  };

  const filtered = filterHealth ? apps.filter(a => a.health === filterHealth) : apps;

  // Group idle stopped apps by space
  const idleApps = apps.filter(a => a.health === 'STOPPED');

  const sendChat = async (text) => {
    const msg = (text || chatInput).trim();
    if (!msg || chatLoading) return;
    setChatInput('');
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setChatLoading(true);
    try {
      const { data } = await api.post('/cf-health/chat', {
        message: msg,
        projectId: selectedProject || null,
      });
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: data.answer,
        meta: `${data.projectName} · ${data.appName}`
      }]);
    } catch (e) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: 'Error: ' + (e.response?.data?.error || e.message),
        error: true
      }]);
    }
    setChatLoading(false);
  };

  const renderMarkdown = (text) => text.split('\n').map((line, i) => {
    if (line.startsWith('## ') || line.startsWith('### '))
      return <div key={i} style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', margin: '10px 0 4px' }}>{line.replace(/^#+\s/, '')}</div>;
    if (line.startsWith('- ') || line.startsWith('* '))
      return <div key={i} style={{ paddingLeft: 14, marginBottom: 3, fontSize: 14 }}>• {line.replace(/^[-*]\s/, '')}</div>;
    if (line.trim() === '') return <div key={i} style={{ height: 6 }} />;
    return <div key={i} style={{ fontSize: 14, marginBottom: 2 }}>{line}</div>;
  });

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>CF Health Dashboard</h1>
          <p style={{ color: '#64748b', fontSize: 14, marginTop: 2 }}>
            Real-time health of all Cloud Foundry apps across spaces
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {lastRefresh && (
            <span style={{ fontSize: 12, color: '#94a3b8' }}>
              Last refreshed: {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <button onClick={() => load(false)} disabled={loading} className="btn-ghost" style={{ padding: '8px 16px', fontSize: 13 }}>
            ↻ Load
          </button>
          <button onClick={() => load(true)} disabled={loading} className="btn-primary" style={{ padding: '8px 16px', fontSize: 13 }}>
            {loading ? '⏳ Refreshing...' : '⚡ Refresh from CF'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid #e2e8f0' }}>
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)} style={{
            padding: '10px 20px', fontSize: 14, fontWeight: 500, border: 'none', cursor: 'pointer',
            background: 'none', borderBottom: tab === i ? '2px solid #1a56db' : '2px solid transparent',
            color: tab === i ? '#1a56db' : '#6b7280', marginBottom: -1
          }}>{t}</button>
        ))}
      </div>

      {/* Chat Tab */}
      {tab === 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 220px)' }}>
          {/* Project selector */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center' }}>
            <label style={{ fontSize: 13, fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>Focus on project:</label>
            <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}
              style={{ fontSize: 13, padding: '6px 10px', minWidth: 200 }}>
              <option value="">Auto-detect from question</option>
              {projects.filter(p => p.cfAppName).map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.cfAppName})</option>
              ))}
            </select>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>or just mention the app name in your question</span>
          </div>

          {/* Chat messages */}
          <div style={{
            flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12,
            padding: '16px', background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0', marginBottom: 12
          }}>
            {messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '75%', padding: '12px 16px', borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: m.role === 'user' ? '#1a56db' : m.error ? '#fee2e2' : '#fff',
                  color: m.role === 'user' ? '#fff' : m.error ? '#dc2626' : '#0f172a',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: m.role === 'assistant' ? '1px solid #e2e8f0' : 'none'
                }}>
                  {m.role === 'assistant' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <span style={{ fontSize: 16 }}>🤖</span>
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>CF Assistant</span>
                      {m.meta && <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 4 }}>· {m.meta}</span>}
                    </div>
                  )}
                  {m.role === 'user'
                    ? <div style={{ fontSize: 14 }}>{m.text}</div>
                    : <div>{renderMarkdown(m.text)}</div>
                  }
                </div>
              </div>
            ))}
            {chatLoading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{ padding: '12px 16px', background: '#fff', borderRadius: '16px 16px 16px 4px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 16 }}>🤖</span>
                    <span style={{ fontSize: 13, color: '#64748b' }}>Fetching CF logs and analyzing</span>
                    <span style={{ fontSize: 18, animation: 'spin 1s linear infinite' }}>⏳</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={chatBottomRef} />
          </div>

          {/* Suggestions */}
          {messages.length <= 1 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              {SUGGESTIONS.map(s => (
                <button key={s} onClick={() => sendChat(s)} style={{
                  padding: '6px 12px', fontSize: 12, borderRadius: 20, border: '1px solid #e2e8f0',
                  background: '#fff', color: '#374151', cursor: 'pointer'
                }}>{s}</button>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendChat()}
              placeholder="Ask about your CF apps... e.g. Why is hr-cap-project slow?"
              style={{ flex: 1, padding: '12px 16px', fontSize: 14, borderRadius: 8, border: '1px solid #e2e8f0', outline: 'none' }}
              disabled={chatLoading}
            />
            <button onClick={() => sendChat()} disabled={chatLoading || !chatInput.trim()}
              className="btn-primary" style={{ padding: '12px 24px', fontSize: 14, borderRadius: 8 }}>
              Send ➤
            </button>
          </div>
        </div>
      )}

      {/* Dashboard Tab */}
      {tab === 0 && (<>

      {error && (
        <div style={{ padding: 12, background: '#fee2e2', color: '#dc2626', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Initial load prompt */}
      {!loading && apps.length === 0 && !error && (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>☁️</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#0f172a', marginBottom: 8 }}>No data loaded yet</div>
          <div style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>Click "Refresh from CF" to fetch live app health from Cloud Foundry</div>
          <button onClick={() => load(true)} className="btn-primary" style={{ padding: '10px 28px' }}>
            ⚡ Refresh from CF
          </button>
        </div>
      )}

      {apps.length > 0 && (<>

        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Total Apps',  value: summary.total,   color: '#1a56db', bg: '#eff6ff', filter: ''          },
            { label: 'Healthy',     value: summary.healthy,  color: '#059669', bg: '#d1fae5', filter: 'HEALTHY'   },
            { label: 'Warning',     value: summary.warning,  color: '#d97706', bg: '#fef3c7', filter: 'WARNING'   },
            { label: 'Critical',    value: summary.critical, color: '#dc2626', bg: '#fee2e2', filter: 'CRITICAL'  },
            { label: 'Stopped',     value: summary.stopped,  color: '#64748b', bg: '#f1f5f9', filter: 'STOPPED'   },
          ].map(c => (
            <div key={c.label} onClick={() => setFilterHealth(filterHealth === c.filter ? '' : c.filter)}
              className="card" style={{
                textAlign: 'center', cursor: 'pointer', padding: '16px 12px',
                border: filterHealth === c.filter ? `2px solid ${c.color}` : '1px solid #e2e8f0',
                background: filterHealth === c.filter ? c.bg : '#fff',
                transition: 'all 0.15s'
              }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: c.color }}>{c.value}</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, fontWeight: 500 }}>{c.label}</div>
            </div>
          ))}
        </div>

        {/* Space Pressure + Idle Apps */}
        {pressure && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>

            {/* Memory Pressure Gauge */}
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ fontWeight: 600, fontSize: 15 }}>Space Instance Pressure</h3>
                <span style={{
                  fontSize: 12, padding: '3px 10px', borderRadius: 20, fontWeight: 600,
                  background: healthBg[pressure.pressureLevel === 'HIGH' ? 'CRITICAL' : pressure.pressureLevel === 'MEDIUM' ? 'WARNING' : 'HEALTHY'],
                  color: pressureColor[pressure.pressureLevel]
                }}>{pressure.pressureLevel}</span>
              </div>

              <div style={{ fontSize: 32, fontWeight: 700, color: pressureColor[pressure.pressureLevel], marginBottom: 4 }}>
                {pressure.instanceUsagePct}%
              </div>
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
                {pressure.runningInstances} running / {pressure.totalInstances} total instances
              </div>

              {/* Progress bar */}
              <div style={{ background: '#f1f5f9', borderRadius: 8, height: 10, overflow: 'hidden' }}>
                <div style={{
                  width: `${pressure.instanceUsagePct}%`, height: '100%', borderRadius: 8,
                  background: pressureColor[pressure.pressureLevel],
                  transition: 'width 0.5s'
                }} />
              </div>

              <div style={{ display: 'flex', gap: 16, marginTop: 14 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#dc2626' }}>{pressure.criticalApps}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Critical</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#94a3b8' }}>{pressure.stoppedApps}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Stopped</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#059669' }}>{summary.healthy}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Healthy</div>
                </div>
              </div>
            </div>

            {/* Idle Apps Suggestion */}
            <div className="card">
              <h3 style={{ fontWeight: 600, fontSize: 15, marginBottom: 12 }}>
                💤 Idle Apps (Stopped)
                <span style={{ fontSize: 12, fontWeight: 400, color: '#94a3b8', marginLeft: 8 }}>
                  {idleApps.length} apps not running
                </span>
              </h3>
              {idleApps.length === 0 ? (
                <p style={{ color: '#9ca3af', fontSize: 13 }}>All apps are running — no idle apps found.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {idleApps.map(a => (
                    <div key={a.appName} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '8px 12px', background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0'
                    }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>{a.appName}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{a.org} / {a.space}</div>
                      </div>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#f1f5f9', color: '#64748b' }}>
                        ⏹ stopped
                      </span>
                    </div>
                  ))}
                  {pressure.pressureLevel === 'HIGH' && (
                    <div style={{ marginTop: 8, padding: '8px 12px', background: '#fef3c7', borderRadius: 6, border: '1px solid #fcd34d', fontSize: 12, color: '#92400e' }}>
                      ⚠️ High pressure detected — consider stopping additional idle apps or scaling down instances.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* App Health Table */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontWeight: 600, fontSize: 15 }}>
              All CF Apps
              {filterHealth && (
                <span style={{ fontSize: 12, fontWeight: 400, color: '#1a56db', marginLeft: 8 }}>
                  — filtered: {filterHealth}
                  <button onClick={() => setFilterHealth('')} style={{ marginLeft: 6, background: 'none', border: 'none', color: '#1a56db', cursor: 'pointer', fontSize: 12 }}>✕</button>
                </span>
              )}
            </h3>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>{filtered.length} apps</span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: '10px 20px', textAlign: 'left', fontSize: 12, color: '#64748b', fontWeight: 600 }}>App Name</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: '#64748b', fontWeight: 600 }}>Health</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: '#64748b', fontWeight: 600 }}>Instances</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: '#64748b', fontWeight: 600 }}>State</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: '#64748b', fontWeight: 600 }}>Space</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: '#64748b', fontWeight: 600 }}>Project</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a.appName} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px 20px', fontWeight: 500, fontSize: 14, color: '#0f172a' }}>
                    {a.appName}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                      background: healthBg[a.health] || '#f8fafc',
                      color: healthColor[a.health] || '#64748b'
                    }}>
                      {healthIcon[a.health]} {a.health}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13 }}>
                    <span style={{ fontWeight: 600, color: a.runningInstances === a.totalInstances && a.totalInstances > 0 ? '#059669' : '#dc2626' }}>
                      {a.runningInstances}
                    </span>
                    <span style={{ color: '#94a3b8' }}>/{a.totalInstances}</span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 12, color: '#64748b' }}>
                    <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>{a.state}</code>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 12, color: '#64748b' }}>{a.space}</td>
                  <td style={{ padding: '12px 16px', fontSize: 12, color: a.projectName ? '#374151' : '#94a3b8' }}>
                    {a.projectName || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>)}

      </>)} {/* end dashboard tab */}
    </div>
  );
}
